import {
  cardContent,
  spokenText,
  timelineSchema,
  transitionSchema,
  type Action,
  type AudioTrack,
  type Background,
  type BookendContent,
  type CaptionStyle,
  type GeneratedScene,
  type Timeline,
  type TranscriptSegment,
  type Transition,
} from "@veyso/core";
import { buildCaptions, captionsFromNarration, type NarrationLine } from "./captions.js";
import { planFocusEffects, type FocusOptions } from "./focus.js";

/** A synthesised narration clip that has already been written to storage. */
export interface VoiceClip {
  sceneId: string;
  assetKey: string;
  duration: number;
}

export interface BookendSpec {
  enabled?: boolean;
  duration: number;
  content: BookendContent;
  background?: Background;
  script?: string;
  voiceId?: string;
  voice?: { assetKey: string; duration: number };
  /**
   * Silences the narration without discarding it. The track is still built, so
   * turning it back on is a toggle rather than a re-synthesis.
   */
  voiceEnabled?: boolean;
  /** Seconds to shift the narration from where it would otherwise land. */
  voiceOffset?: number;
}

export interface BuildTimelineInput {
  projectId?: string;
  projectName?: string;
  fps?: 24 | 25 | 30 | 50 | 60;
  width?: number;
  height?: number;
  body: {
    assetKey: string;
    /** Playable length of the recording, after any trim. */
    duration: number;
    trimStart?: number;
    fit?: "cover" | "contain";
    /** width ÷ height of the recording, so a framed card can match its shape. */
    aspect?: number;
    /**
     * Stretches of the recording to play, in order. Omitted plays all of it.
     * `duration` must be their total when these are given.
     */
    segments?: { start: number; end: number }[];
    /** Constant-frame-rate copy for the editor preview; absent is fine. */
    proxyAssetKey?: string;
    /**
     * The recording's audio, extracted to its own file during ingest. Absent
     * when the recording is silent, in which case no original track is created
     * — an empty layer is worse than no layer.
     */
    audioAssetKey?: string;
    originalAudio?: { enabled?: boolean; volume?: number };
    voiceId?: string;
    background?: Background;
  };
  scenes?: NarrationScene[];
  voiceClips?: VoiceClip[];
  actions?: Action[];
  captions?: { enabled?: boolean; segments?: TranscriptSegment[]; style?: Partial<CaptionStyle> };
  head?: BookendSpec;
  end?: BookendSpec;
  transitions?: { headToBody?: Partial<Transition>; bodyToEnd?: Partial<Transition> };
  focus?: Partial<FocusOptions>;
  music?: { assetKey: string; volume?: number };
}

export interface BuildTimelineResult {
  timeline: Timeline;
  /** Things the user should know about but that do not stop a render. */
  warnings: string[];
}

/**
 * A narration line as the timeline places it.
 *
 * `pauseBefore` is not part of `GeneratedScene` on purpose: that type is the
 * shape a model returns, and a pause is a decision someone made about pacing.
 * Keeping it off the model's output means the JSON schema handed to the
 * provider stays exactly what it is allowed to fill in.
 */
export type NarrationScene = GeneratedScene & { pauseBefore?: number };

/**
 * Packs narration clips into the BODY section.
 *
 * The recording is the visual truth, so its length is fixed. Clips are laid down
 * in scene order starting no earlier than the moment they describe and never
 * overlapping the previous clip. A clip that would run past the end of the
 * recording is pulled earlier; one that cannot fit at all is dropped with a
 * warning rather than silently overrunning the video.
 */
function packBodyVoice(
  scenes: NarrationScene[],
  clips: VoiceClip[],
  bodyDuration: number,
  offset: number,
  /**
   * The earliest the first line may speak, in BODY-local seconds.
   *
   * BODY starts before HEAD has finished playing, because the transition
   * between them is a cross-fade. Without this the first body line lands
   * underneath the tail of the HEAD narration and the two talk over each other.
   */
  floor = 0,
): { tracks: AudioTrack[]; warnings: string[]; lines: NarrationLine[] } {
  const byScene = new Map(clips.map((clip) => [clip.sceneId, clip]));
  const tracks: AudioTrack[] = [];
  const warnings: string[] = [];
  // What each clip says, so captions can be built from the words the viewer
  // will actually hear rather than from the transcript.
  const lines: NarrationLine[] = [];
  let cursor = Math.max(0, floor);

  for (const scene of [...scenes].sort((a, b) => a.sourceStart - b.sourceStart)) {
    const clip = byScene.get(scene.id);
    if (!clip || clip.duration <= 0) continue;

    if (clip.duration > bodyDuration) {
      warnings.push(`Narration for "${scene.id}" is longer than the whole recording and was left out.`);
      continue;
    }

    // A pause is added to wherever the line would have landed, not treated as a
    // minimum gap: swallowed by an existing silence it would do nothing in the
    // very place someone reaches for it.
    const pause = Math.max(0, scene.pauseBefore ?? 0);
    const wanted = Math.max(scene.sourceStart, cursor) + pause;

    /**
     * Never before the line before it has finished.
     *
     * This used to pull a line back over its neighbour when it would otherwise
     * run past the end of the recording, and warn. The warning was true and
     * the result was still two people talking at once — which is not a layout
     * quirk, it is a defect in the audio somebody exports and sends to a
     * customer. There is no length of recording for which that is the best
     * available output.
     *
     * So the sequence is the invariant. What gives instead is the tail: a line
     * that will not fit is shortened, below. The real answer is upstream —
     * narration written to the time available, and rewritten when it does not
     * fit — and this is what guarantees the defect cannot reach a video even
     * when that fails.
     */
    const start = wanted;

    // The cost of the pause, stated. The recording does not wait, so seconds
    // spent here are seconds every later line spends too — and a line pushed
    // far enough stops describing what is on screen while it is spoken.
    if (pause > 0 && start > scene.sourceEnd) {
      warnings.push(
        `A pause pushed the narration for "${scene.id}" past the moment it describes, by ` +
          `${(start - scene.sourceEnd).toFixed(1)}s.`,
      );
    }

    // Nothing is left of the recording to speak over.
    if (start >= bodyDuration - 0.05) {
      warnings.push(
        `Narration for "${scene.id}" had nowhere left in the recording and was left out. ` +
          `Shorten the narration before it, or record for longer.`,
      );
      continue;
    }

    /**
     * Cut to what is left, rather than moved on top of its neighbour.
     *
     * A line that stops early is a line somebody can hear is wrong and go and
     * fix. Two lines playing together is a muddle that sounds like a broken
     * product, and which of the two you are hearing is not recoverable by ear.
     */
    let duration = clip.duration;
    if (start + duration > bodyDuration) {
      const room = bodyDuration - start;
      warnings.push(
        `Narration for "${scene.id}" is ${(duration - room).toFixed(1)}s longer than the recording has left ` +
          `and was cut short. Shorten it and re-voice to hear all of it.`,
      );
      duration = room;
    }

    tracks.push({
      id: `voice-${scene.id}`,
      role: "body-voice",
      enabled: true,
      assetKey: clip.assetKey,
      startAt: Number((offset + start).toFixed(3)),
      duration: Number(duration.toFixed(3)),
      trimStart: 0,
      volume: 1,
      fadeIn: 0.05,
      fadeOut: 0.05,
      loop: false,
    });
    lines.push({
      // What is heard, not what is written: a caption reading "[1.5s]" out to
      // the viewer would be the marker escaping the one place it belongs.
      text: spokenText(scene.script),
      startAt: Number((offset + start).toFixed(3)),
      duration: Number(duration.toFixed(3)),
    });
    cursor = start + duration;
  }

  return { tracks, warnings, lines };
}

/**
 * Where a bookend's narration actually speaks.
 *
 * The section positions are the *picture's* clock, and transitions deliberately
 * overlap: BODY starts before HEAD has finished and END starts before BODY has.
 * Pinning the voice to those same positions is what made the outro talk over
 * the last line of the body. The voice instead waits for the narration before
 * it to finish, then stays inside its own card.
 *
 * Exported because the worker re-attaches bookend voice after synthesis and has
 * to land on the same answer; the two used to compute this separately and had
 * already drifted apart once.
 */
export function placeBookendVoice(input: {
  /** Where the card itself starts on the finished video's clock. */
  sectionStart: number;
  sectionDuration: number;
  voiceDuration: number;
  /** End of the narration immediately before this one, on the same clock. */
  after?: number;
  offset?: number;
  label: "HEAD" | "END";
}): { startAt: number; duration: number; warnings: string[] } {
  const { sectionStart, sectionDuration, label } = input;
  const warnings: string[] = [];
  const sectionEnd = sectionStart + sectionDuration;

  // Never longer than the card it belongs to; a voice that outlives its own
  // picture would keep talking over whatever came next.
  let duration = Math.min(input.voiceDuration, sectionDuration);
  if (input.voiceDuration > sectionDuration) {
    warnings.push(`The ${label} narration is longer than the ${label} section and will be cut short.`);
  }

  // Wait for the previous voice, then apply whatever the user asked for.
  const natural = Math.max(sectionStart, input.after ?? 0);
  let startAt = natural + (input.offset ?? 0);

  if (startAt < sectionStart) {
    warnings.push(`The ${label} narration was moved back to the start of the ${label} card.`);
    startAt = sectionStart;
  }
  if (startAt + duration > sectionEnd) {
    const latest = sectionEnd - duration;
    if (latest < sectionStart) {
      // Only reachable if the card is shorter than the voice, already warned.
      startAt = sectionStart;
      duration = sectionDuration;
    } else {
      if (startAt > latest + 0.001) {
        warnings.push(
          `The ${label} narration would have run past the end of the ${label} card and was pulled ` +
            `${(startAt - latest).toFixed(1)}s earlier.`,
        );
      }
      startAt = latest;
    }
  }

  return { startAt: Number(startAt.toFixed(3)), duration: Number(duration.toFixed(3)), warnings };
}

/**
 * Moves a bookend's narration on an already-built timeline, in place.
 *
 * Saving a timeline does not re-run the builder — the editor writes the JSON it
 * is holding straight back. So an editor that only recorded the offset would
 * leave the clip sitting where it was until something else rebuilt the project,
 * and the control would look broken on every project that already exists.
 *
 * This changes both halves at once: the offset, which is what a later rebuild
 * reads, and the track, which is what the editor and the preview draw. It uses
 * `placeBookendVoice`, so a drag and a rebuild cannot disagree.
 */
export function applyBookendVoiceOffset(timeline: Timeline, which: "head" | "end", offset: number): void {
  const section = timeline.sections[which];
  const role = which === "head" ? "head-voice" : "end-voice";
  const track = timeline.audio.find((candidate) => candidate.role === role);
  if (!section || !track) return;

  const clamped = Math.min(30, Math.max(-30, offset));
  const bodyStart =
    (timeline.sections.head?.enabled ? timeline.sections.head.duration : 0) -
    (timeline.transitions.headToBody?.duration ?? 0);
  const sectionStart =
    which === "head"
      ? 0
      : bodyStart + timeline.sections.body.duration - (timeline.transitions.bodyToEnd?.duration ?? 0);

  const placed = placeBookendVoice({
    sectionStart: Math.max(0, sectionStart),
    sectionDuration: section.duration,
    voiceDuration: track.duration,
    after:
      which === "head"
        ? 0
        : timeline.audio
            .filter((candidate) => candidate.role === "body-voice" && candidate.enabled)
            .reduce((latest, candidate) => Math.max(latest, candidate.startAt + candidate.duration), 0),
    offset: clamped,
    label: which === "head" ? "HEAD" : "END",
  });

  section.voiceOffset = Number(clamped.toFixed(3));
  track.startAt = placed.startAt;
  track.duration = placed.duration;
}

/**
 * Switches between the recording's own audio and the BODY narration.
 *
 * Both halves together, for the same reason as the bookend voice toggle: saving
 * a timeline does not re-run the builder, so flipping only the original's flag
 * would leave the narration playing over it until something else rebuilt it.
 */
export function setOriginalAudio(timeline: Timeline, enabled: boolean): void {
  const original = timeline.audio.find((track) => track.role === "original");
  if (!original) return;

  original.enabled = enabled;
  for (const track of timeline.audio) {
    if (track.role === "body-voice") track.enabled = !enabled;
  }
}

/** Transitions cannot be longer than the shorter of the two sections they join. */
function clampTransition(
  partial: Partial<Transition> | undefined,
  a: number,
  b: number,
  fallback: Partial<Transition>,
): Transition {
  const parsed = transitionSchema.parse({ ...fallback, ...partial });
  const maximum = Math.max(0, Math.min(a, b) - 0.05);
  return { ...parsed, duration: Number(Math.min(parsed.duration, maximum).toFixed(3)) };
}

/**
 * Assembles the timeline that both the editor preview and the final render
 * consume. Nothing here calls a provider: by this point every asset already
 * exists, which is what makes rendering deterministic.
 */
export function buildTimeline(input: BuildTimelineInput): BuildTimelineResult {
  const warnings: string[] = [];
  const fps = input.fps ?? 30;
  const bodyDuration = Math.max(0.1, input.body.duration);

  const head = input.head?.enabled === false ? undefined : input.head;
  const end = input.end?.enabled === false ? undefined : input.end;

  const headToBody = head
    ? clampTransition(input.transitions?.headToBody, head.duration, bodyDuration, { type: "zoom", duration: 0.6 })
    : undefined;
  const bodyToEnd = end
    ? clampTransition(input.transitions?.bodyToEnd, bodyDuration, end.duration, { type: "fade", duration: 0.8 })
    : undefined;

  // Where each section sits on the finished video's clock. Transitions overlap
  // their neighbours, so BODY starts before HEAD has finished playing.
  const bodyStart = head ? Math.max(0, head.duration - (headToBody?.duration ?? 0)) : 0;
  const endStart = end ? Math.max(0, bodyStart + bodyDuration - (bodyToEnd?.duration ?? 0)) : 0;

  const scenes = input.scenes ?? [];
  const voiceClips = input.voiceClips ?? [];

  // HEAD speaks first and from zero, so it is placed before anything else and
  // the body's first line is held until it has finished. A silenced HEAD holds
  // nothing back — it is not heard, so there is nothing to talk over.
  const headVoice =
    head?.voice && head.voiceEnabled !== false
      ? placeBookendVoice({
          sectionStart: 0,
          sectionDuration: head.duration,
          voiceDuration: head.voice.duration,
          offset: head.voiceOffset,
          label: "HEAD",
        })
      : undefined;

  const {
    tracks: bodyVoice,
    warnings: voiceWarnings,
    lines: narrationLines,
  } = packBodyVoice(
    scenes,
    voiceClips,
    bodyDuration,
    bodyStart,
    headVoice ? headVoice.startAt + headVoice.duration - bodyStart : 0,
  );
  warnings.push(...voiceWarnings);

  const audio: AudioTrack[] = [...bodyVoice];

  /** Where the last body line stops speaking, on the finished video's clock. */
  const bodyVoiceEnd = bodyVoice.reduce((latest, track) => Math.max(latest, track.startAt + track.duration), 0);

  if (head?.voice) {
    // Placed even when silenced, so the editor can show the track and turning it
    // back on does not need a re-synthesis.
    const placed =
      headVoice ??
      placeBookendVoice({
        sectionStart: 0,
        sectionDuration: head.duration,
        voiceDuration: head.voice.duration,
        offset: head.voiceOffset,
        label: "HEAD",
      });
    audio.push({
      id: "voice-head",
      role: "head-voice",
      enabled: head.voiceEnabled !== false,
      assetKey: head.voice.assetKey,
      startAt: placed.startAt,
      duration: placed.duration,
      trimStart: 0,
      volume: 1,
      fadeIn: 0.1,
      fadeOut: 0.1,
      loop: false,
    });
    if (head.voiceEnabled !== false) warnings.push(...placed.warnings);
  }

  if (end?.voice) {
    const placed = placeBookendVoice({
      sectionStart: endStart,
      sectionDuration: end.duration,
      voiceDuration: end.voice.duration,
      // The outro waits for the body to stop talking rather than starting when
      // the cross-fade does.
      after: bodyVoiceEnd,
      offset: end.voiceOffset,
      label: "END",
    });
    audio.push({
      id: "voice-end",
      role: "end-voice",
      enabled: end.voiceEnabled !== false,
      assetKey: end.voice.assetKey,
      startAt: placed.startAt,
      duration: placed.duration,
      trimStart: 0,
      volume: 1,
      fadeIn: 0.1,
      fadeOut: 0.1,
      loop: false,
    });
    if (end.voiceEnabled !== false) warnings.push(...placed.warnings);
  }

  // Narration replaces the recording's own audio rather than talking over it.
  // The track is still created — muted, not omitted — so the editor can show it
  // as a layer and the user can bring it back without a rebuild.
  //
  // With no narration it plays at full volume: a silent video is never what the
  // user meant.
  const originalAudioEnabled = input.body.originalAudio?.enabled ?? bodyVoice.length === 0;

  // One or the other, never both. Bringing the recording's sound back mutes the
  // narration the same way narration muted it — kept as layers, so switching
  // back is one toggle. See `audibleTracks`.
  if (input.body.audioAssetKey && originalAudioEnabled) {
    for (const track of bodyVoice) track.enabled = false;
  }

  // Full volume either way: with nothing talking over it there is nothing to
  // duck under.
  const originalAudioVolume = input.body.originalAudio?.volume ?? 1;
  if (input.body.audioAssetKey) {
    audio.push({
      id: "audio-original",
      role: "original",
      assetKey: input.body.audioAssetKey,
      enabled: originalAudioEnabled,
      startAt: Number(bodyStart.toFixed(3)),
      duration: bodyDuration,
      trimStart: input.body.trimStart ?? 0,
      volume: originalAudioVolume,
      fadeIn: 0.1,
      fadeOut: 0.4,
      loop: false,
    });
  } else if (originalAudioEnabled && bodyVoice.length === 0) {
    warnings.push("The recording has no audio track, so this video is silent until you generate narration.");
  }

  const totalDuration = end ? endStart + end.duration : bodyStart + bodyDuration;

  if (input.music) {
    audio.push({
      id: "audio-music",
      role: "music",
      enabled: true,
      assetKey: input.music.assetKey,
      startAt: 0,
      duration: Number(totalDuration.toFixed(3)),
      trimStart: 0,
      volume: input.music.volume ?? 0.12,
      fadeIn: 1,
      fadeOut: 1.5,
      loop: true,
    });
  }

  const actions = input.actions ?? [];
  const focus = planFocusEffects(actions, bodyDuration, input.focus);
  const untargeted = actions.filter((action) => !action.target).length;
  if (untargeted > 0) {
    warnings.push(
      `${untargeted} detected action${untargeted === 1 ? "" : "s"} had no on-screen target, so no focus effect was added. Place the focus box in the editor to enable one.`,
    );
  }

  const timeline = timelineSchema.parse({
    version: 1,
    fps,
    width: input.width ?? 1920,
    height: input.height ?? 1080,
    sections: {
      head: head
        ? {
            id: "head",
            type: "head",
            enabled: true,
            duration: head.duration,
            content: head.content,
            background: head.background,
            script: head.script ?? "",
            voiceId: head.voiceId,
          }
        : undefined,
      body: {
        id: "body",
        type: "body",
        enabled: true,
        assetKey: input.body.assetKey,
        proxyKey: input.body.proxyAssetKey,
        trimStart: input.body.trimStart ?? 0,
        duration: bodyDuration,
        fit: input.body.fit ?? "contain",
        aspect: input.body.aspect,
        segments: input.body.segments ?? [],
        background: input.body.background,
        actions,
        focus,
        script: scenes.map((scene) => scene.script).join(" "),
        voiceId: input.body.voiceId,
      },
      end: end
        ? {
            id: "end",
            type: "end",
            enabled: true,
            duration: end.duration,
            content: end.content,
            background: end.background,
            script: end.script ?? "",
            voiceId: end.voiceId,
          }
        : undefined,
    },
    transitions: { headToBody, bodyToEnd },
    audio,
    captions: {
      enabled: input.captions?.enabled ?? true,
      style: input.captions?.style ?? {},
      // Narration wins when it exists: the transcript is what the presenter
      // ad-libbed, the narration is what the viewer hears, and captioning the
      // wrong one puts different words on screen from the ones being spoken.
      cues:
        narrationLines.length > 0
          ? captionsFromNarration(narrationLines)
          : buildCaptions(input.captions?.segments ?? [], bodyDuration).map((cue) => ({
              ...cue,
              // Transcript times are relative to the recording; audio and
              // caption times are both stored on the finished video's clock.
              start: Number((cue.start + bodyStart).toFixed(3)),
              end: Number((cue.end + bodyStart).toFixed(3)),
              words: cue.words?.map((word) => ({
                ...word,
                start: Number((word.start + bodyStart).toFixed(3)),
                end: Number((word.end + bodyStart).toFixed(3)),
              })),
            })),
    },
    meta: {
      projectId: input.projectId,
      projectName: input.projectName,
    },
  });

  return { timeline, warnings };
}

/** A minimal, valid timeline for a recording with nothing else configured yet. */
export function newBodyTimeline(assetKey: string, duration: number): Timeline {
  return buildTimeline({ body: { assetKey, duration } }).timeline;
}

/** Sensible starting cards so a new project has something to preview. */
export function defaultHead(title: string): BookendSpec {
  return {
    enabled: true,
    duration: 4,
    content: cardContent(title),
    script: "",
  };
}

export function defaultEnd(): BookendSpec {
  return {
    enabled: true,
    duration: 5,
    content: cardContent("Thanks for watching"),
    script: "",
  };
}
